import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, CheckCircle } from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

interface BudgetVsActualData {
  category_name: string;
  budget_amount: number;
  actual_amount: number;
  variance: number;
  variance_percentage: number;
  color: string;
}

interface BudgetVsActualResponse {
  data: BudgetVsActualData[];
  month_name: string;
  year: number;
  total_budget: number;
  total_actual: number;
}

const BudgetVsActualChart: React.FC = () => {
  const [data, setData] = useState<BudgetVsActualResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth()); // Current month
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = [2023, 2024, 2025];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response: BudgetVsActualResponse = await analyticsApi.getBudgetVsActual(selectedMonth, selectedYear);
        setData(response);
        setError(null);
      } catch (err) {
        setError('Failed to load budget vs actual data');
        console.error('Error fetching budget vs actual data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth, selectedYear]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getVarianceIcon = (percentage: number) => {
    if (percentage > 10) return <TrendingUp fontSize="small" />;
    if (percentage < -10) return <TrendingDown fontSize="small" />;
    return <CheckCircle fontSize="small" />;
  };

  const getVarianceColor = (percentage: number) => {
    if (percentage > 10) return '#f44336'; // Red for over budget
    if (percentage < -10) return '#4caf50'; // Green for under budget
    return '#ff9800'; // Orange for close to budget
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const budgetData = payload.find((p: any) => p.dataKey === 'budget_amount');
      const actualData = payload.find((p: any) => p.dataKey === 'actual_amount');
      
      if (budgetData && actualData) {
        const variance = actualData.value - budgetData.value;
        const variancePercentage = budgetData.value > 0 
          ? ((variance / budgetData.value) * 100).toFixed(1)
          : '0.0';

        return (
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              p: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {label}
            </Typography>
            <Typography variant="body2">
              Budget: {formatCurrency(budgetData.value)}
            </Typography>
            <Typography variant="body2">
              Actual: {formatCurrency(actualData.value)}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: getVarianceColor(Number(variancePercentage)),
                fontWeight: 'bold',
                mt: 1
              }}
            >
              Variance: {formatCurrency(variance)} ({variancePercentage}%)
            </Typography>
          </Box>
        );
      }
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Budget vs Actual
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <Typography variant="body1" color="text.secondary">
            No budget data available for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Sort data by variance percentage (worst performers first)
  const sortedData = [...data.data].sort((a, b) => Math.abs(b.variance_percentage) - Math.abs(a.variance_percentage));

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Budget vs Actual - {data.month_name} {data.year}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100 } }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      <Box sx={{ height: { xs: 200, sm: 250, md: 280 }, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={sortedData} margin={{ top: 20, right: 20, left: 15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="category_name"
              stroke="#666"
              fontSize={10}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="#666"
              fontSize={11}
              width={55}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="budget_amount" 
              fill="#3f51b5" 
              name="Budget"
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="actual_amount" 
              fill="#f44336" 
              name="Actual"
              radius={[2, 2, 0, 0]}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Summary and Variance Indicators */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Total Budget
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3f51b5' }}>
              {formatCurrency(data.total_budget)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Total Actual
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f44336' }}>
              {formatCurrency(data.total_actual)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Overall Variance
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: data.total_actual > data.total_budget ? '#f44336' : '#4caf50'
              }}
            >
              {formatCurrency(data.total_actual - data.total_budget)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Category Performance:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {sortedData.slice(0, 6).map((item) => (
            <Chip
              key={item.category_name}
              size="small"
              icon={getVarianceIcon(item.variance_percentage)}
              label={`${item.category_name}: ${item.variance_percentage >= 0 ? '+' : ''}${item.variance_percentage}%`}
              sx={{
                backgroundColor: getVarianceColor(item.variance_percentage),
                color: 'white',
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default BudgetVsActualChart;